import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Row, Col, Checkbox } from 'antd';
import 'antd/es/date-picker/style/index.less';
import { LocalizationContext } from '../../../hoc/Localization';
import { LocationContext } from '../../../hoc/Location';
import { SearchContext } from '../../../hoc/Search';
import Button from '../../Button';
import RangePicker from '../../Input/RangePicker';
import Select from '../../Select';
import Price from './Price';
import Rating from './Rating';
import Router from '../../../routes';
import styles from './styles.less';

const Filters = ({ options, defaultCategories }) => {
  const { filtersVisible, toggleFilters, pushFilters, filters } = React.useContext(SearchContext);
  const { locale, t, translate } = React.useContext(LocalizationContext);
  const { location } = React.useContext(LocationContext);
  const { center, zoom } = location;
  const {
    categories = {},
    courseTypes = [],
    languages = [],
    dayTimes = [],
    levels = [],
    frequency = [],
    loaded = false,
  } = options;

  const [stateFilters, setStateFilters] = React.useState(filters);

  React.useEffect(() => {
    toggleFilters(false);
  }, []);

  const selectedCategory = defaultCategories.find(category => category.label === filters.category);

  const selects = [
    {
      label: 'category',
      mobile: true,
      options: Object.keys(categories).map(category => ({
        title: t(categories[category]),
        value: category,
      })),
    },

    {
      label: 'courseType',
      options: courseTypes
        .filter(value => !selectedCategory || value.category === selectedCategory.id)
        .map(type => ({
          title: translate(type, 'title'),
          value: type.title,
        })),
    },

    {
      label: 'language',
      options: languages
        .filter(value => !selectedCategory || value.category === selectedCategory.id)
        .map(language => ({
          title: translate(language, 'Name'),
          value: language.Name,
        })),
    },

    {
      label: 'level',
      options: levels
        .filter(value => !selectedCategory || value.category === selectedCategory.id)
        .map(level => ({
          title: `${level.key} | ${level.value}`,
          value: level.key,
        })),
    },

    {
      label: 'frequency',
      options: [...frequency],
    },
  ];

  const submit = () => {
    pushFilters({
      ...stateFilters,
    });
  };

  const reset = () => {
    const queryParams = { ...center, zoom };

    if (filters.search) {
      queryParams.search = filters.search;
    }

    Router.pushRoute('search', { locale, queryParams });
  };

  const defaultValue = filters.date
    ? [
      filters.date[0] ? moment(filters.date[0]) : null,
      filters.date[1] ? moment(filters.date[1]) : null,
    ]
    : null;

  return (
    <div className={[styles.filters, filtersVisible ? styles.active : null].join(' ')}>
      <div className={styles.overlay} onClick={() => toggleFilters()} />

      <div className={styles.inner}>
        {loaded ? (
          <>
            <section>
              <h3>Course</h3>

              <Row type="flex" gutter={70} className={styles.row}>
                {selects
                  .filter(select => !!select.options.length)
                  .map(select => (
                    <Col
                      className={select.mobile ? 'mobile' : null}
                      key={select.label}
                      xs={24}
                      md={12}
                    >
                      <Select
                        version="default"
                        placeholder={t(`search.${select.label}`)}
                        defaultValue={filters[select.label]}
                        allowClear
                        showSearch
                        options={select.options}
                        onChange={value => {
                          setStateFilters({ ...stateFilters, [select.label]: value });
                        }}
                      />
                    </Col>
                  ))}

                <Col xs={24}>
                  <RangePicker
                    defaultValue={defaultValue}
                    placeholder={[t('course.details.content.start_date'), t('form.dates.end')]}
                    onChange={dates => {
                      const date =
                        dates && dates[0]
                          ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]
                          : null;
                      setStateFilters({ ...stateFilters, date });
                    }}
                    className={styles.datePicker}
                  />
                </Col>
              </Row>
            </section>

            <section>
              <Row>
                <Col xs={12}>
                  <Checkbox.Group
                    style={{ width: '100%' }}
                    defaultValue={filters.time}
                    onChange={time => {
                      setStateFilters({ ...stateFilters, time });
                    }}
                  >
                    {dayTimes.map(dayTime => (
                      <p key={dayTime.title}>
                        <Checkbox value={dayTime.title}>
                          {t(`details.course.${dayTime.title.toLowerCase()}`) || dayTime.title}
                        </Checkbox>
                      </p>
                    ))}
                  </Checkbox.Group>
                </Col>

                <Col xs={12}>
                  <p>
                    <Checkbox
                      defaultChecked={filters.online}
                      onChange={e => {
                        const online = e.target.checked;
                        setStateFilters({ ...stateFilters, online });
                      }}
                    >
                      {t('search.online')}
                    </Checkbox>
                  </p>
                </Col>
                <Col xs={12}>
                  <p>
                    <Checkbox
                      defaultChecked={filters.discountedPrice}
                      onChange={e => {
                        const discountedPrice = e.target.checked;
                        setStateFilters({ ...stateFilters, discountedPrice });
                      }}
                    >
                      {t('search.discounted')}
                    </Checkbox>
                  </p>
                </Col>
                {/* <Col>
                  <p>
                    <Checkbox
                      defaultChecked={filters.early_bird_discount}
                      onChange={e => {
                        const earlyBirdDiscount = e.target.checked;
                        setStateFilters({ ...stateFilters, earlyBirdDiscount });
                      }}
                    >
                      early bird date
                    </Checkbox>
                  </p>
                </Col>
                <Col xs={12}>
                  <p>
                    <Checkbox
                      defaultChecked={filters.price}
                      onChange={e => {
                        const price = e.target.checked;
                        setStateFilters({ ...stateFilters, price });
                      }}
                    >
                      price
                    </Checkbox>
                  </p>
                </Col>
                <Col xs={12}>
                  <p>
                    <Checkbox
                      defaultChecked={filters.last_minute_discount}
                      onChange={e => {
                        const lastMinuteDiscount = e.target.checked;
                        setStateFilters({ ...stateFilters, lastMinuteDiscount });
                      }}
                    >
                      last minute
                    </Checkbox>
                  </p>
                </Col> */}
              </Row>
            </section>

            <section className="mobile">
              <h3>{t('search.price')}</h3>

              <Price
                price={filters.price}
                setPrice={price => {
                  setStateFilters({ ...stateFilters, price });
                }}
                freeTrial={filters.free_trial}
                // eslint-disable-next-line
                setFreeTrial={free_trial => {
                  setStateFilters({ ...stateFilters, free_trial });
                }}
              />
            </section>

            <section className="mobile">
              <h3>{t('ratings')}</h3>

              <Rating
                rating={filters.rating}
                setRating={rating => {
                  setStateFilters({ ...stateFilters, rating });
                }}
              />
            </section>

            <section>
              <a className={styles.reset} onClick={() => reset()}>
                {t('search.filters.reset')}
              </a>
            </section>

            <section className={styles.buttons}>
              <Button type="secondary" onClick={() => toggleFilters()}>
                {t('form.cancel')}
              </Button>

              <Button type="primary" onClick={() => submit()}>
                {t('addschool.confirm.submit')}
              </Button>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

Filters.propTypes = {
  defaultCategories: PropTypes.array,
  options: PropTypes.object,
};

Filters.defaultProps = {
  defaultCategories: [],
  options: {},
};

export default Filters;
